// Standardized run result — see FD §17.3.
//
// Every executor (Codex CLI, Claude CLI, OpenAI-compatible API, ...) must
// report back through this exact shape so the flow engine can validate a
// `recommended_next_state` before updating any external system.

import { z } from 'zod'

export const RunResultStatus = {
  Completed: 'completed',
  Blocked: 'blocked',
  Failed: 'failed',
  Cancelled: 'cancelled',
  TimedOut: 'timed_out',
} as const

export type RunResultStatus =
  (typeof RunResultStatus)[keyof typeof RunResultStatus]

export const RunResultStatusSchema = z.nativeEnum(RunResultStatus)

export const TestSummarySchema = z.object({
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative().optional(),
})

export const ArtifactRefSchema = z.object({
  name: z.string(),
  path: z.string(),
  sizeBytes: z.number().int().nonnegative().optional(),
  contentType: z.string().optional(),
})

export const PullRequestRefSchema = z
  .object({
    url: z.string().url(),
    number: z.number().int().positive(),
    isDraft: z.boolean(),
  })
  .nullable()

/**
 * Mirrors the example in FD §17.3:
 *
 * ```json
 * {
 *   "status": "completed",
 *   "summary": "Validatie toegevoegd aan de import-API",
 *   "changed_files": ["src/import/validate.ts"],
 *   "tests": { "passed": 48, "failed": 0 },
 *   "artifacts": [],
 *   "branch": "agent/ROLEE-123-import-validation",
 *   "commit": "abc123",
 *   "pull_request": null,
 *   "blockers": [],
 *   "recommended_next_state": "Test"
 * }
 * ```
 */
export const RunResultSchema = z.object({
  status: RunResultStatusSchema,
  summary: z.string(),
  changed_files: z.array(z.string()),
  tests: TestSummarySchema.nullable(),
  artifacts: z.array(ArtifactRefSchema),
  branch: z.string().nullable(),
  commit: z.string().nullable(),
  pull_request: PullRequestRefSchema,
  blockers: z.array(z.string()),
  /**
   * Proposed next workflow state (e.g. a Plane project state name). The flow
   * engine must validate this against the project's ProjectPolicy.workflow
   * before applying it to any external system (FD §17.3, §13.2) — that
   * validation is business logic and intentionally not implemented here.
   */
  recommended_next_state: z.string().nullable(),
  /** What happened to the branch after the run: pushed or not, and where. */
  delivery: z
    .object({ pushed: z.boolean(), repositoryUrl: z.string().url().optional() })
    .optional(),
})

export type RunResult = z.infer<typeof RunResultSchema>

/** Throwing parse — use in the worker right after an executor reports back. */
export function parseRunResult(input: unknown): RunResult {
  return RunResultSchema.parse(input)
}

/** Non-throwing parse — use in the API when validating externally-supplied results. */
export function safeParseRunResult(input: unknown) {
  return RunResultSchema.safeParse(input)
}
